<script setup>
import {computed, defineProps, defineEmits, ref, toRefs} from "vue"
import HeaderRender from "@/components/Blocks/Render/HeaderRender.vue";
import TextareaRender from "@/components/Blocks/Render/TextareaRender.vue";
import TinyRender from "@/components/Blocks/Render/TinyRender.vue";
import ImageRender from "@/components/Blocks/Render/ImageRender.vue";
import TinymceTypeForm from "@/components/Blocks/Types/TinymceTypeForm.vue";
import ImageTypeForm from "@/components/Blocks/Types/ImageTypeForm.vue";
import HeaderTypeForm from "@/components/Blocks/Types/HeaderTypeForm.vue";
import TextareaTypeForm from "@/components/Blocks/Types/TextareaTypeForm.vue";
import ColumnTypeForm from "@/components/Blocks/Types/ColumnTypeForm.vue";
import BlockService from "@/services/blockService";
import ColumnRender from "@/components/Blocks/Render/ColumnRender.vue";


const modalVisible = ref(false);

const components = {
  Heading: HeaderRender,
  TextareaRender: TextareaRender,
  TinyRender: TinyRender,
  ImageRender: ImageRender,
  ColumnRender: ColumnRender
}

const props = defineProps({
  blocks: {},
	competitionId: {
		type: [String, Number],
		required: true
	}
});
const {blocks, competitionId} = toRefs(props);

const emit = defineEmits(['addBlock', 'removeBlock', 'moveBlockUp', 'moveBlockDown', 'handleOk', 'saveBlockData'])


function addBlock(object) {
  emit('addBlock', object);
}

async function removeBlock(block, index) {
  if (confirm('Вы уверены, что хотите удалить этот блок?')) {
    await BlockService.clearBlockData(block);
    emit('removeBlock', index);
  }
}

function moveBlockUp(index) {
  if (index > 0) {
    emit('moveBlockUp', index);
  }
}

function moveBlockDown(index) {
  emit('moveBlockDown', index);
}

const currentBlock = ref({});
const currentIndex = ref(undefined);

function editBlock(block, index) {
  currentIndex.value = index;
  currentBlock.value = block;
  modalVisible.value = true;
}

function handleOk() {
  emit('handleOk', currentIndex.value, currentBlock.value)
  modalVisible.value = false
  currentBlock.value = {}
}

function handleCancel() {
  modalVisible.value = false
  currentBlock.value = {}
}

function saveBlock(index, block) {
  currentIndex.value = index;
  currentBlock.value = block;
  emit('saveBlockData', currentIndex.value, currentBlock.value)
}


const getTypeFormComponent = computed(() => {
  switch (currentBlock.value.type) {
    case 'Heading':
      return HeaderTypeForm
    case 'TextareaRender':
      return TextareaTypeForm
    case 'TinyRender':
      return TinymceTypeForm
    case 'ImageRender':
      return ImageTypeForm
    case 'ColumnRender':
      return ColumnTypeForm
    default:
      return null
  }
})

</script>

<template>
  <div>
    <div v-if="blocks.length > 0">

      <div v-for="(block, index) in blocks" :key="block.id" class="block-wrapper">
        <component
            :is="components[block.type]"
            :data="block.data"
            @saveBlock="(event)=> saveBlock(index, event)"
            :competitionId="competitionId"
        />
        <div class="block-controls">
          <button @click="moveBlockUp(index)" class="move-block-btn" :disabled="index === 0">↑</button>
          <button @click="moveBlockDown(index)" class="move-block-btn"
                  :disabled="index === blocks.length - 1">↓
          </button>
          <span @click="editBlock(block, index)" class="edit-block-btn">
            <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
              <path d="M22.6589 1.34082C22.2285 0.911377 21.7326 0.580566 21.1709 0.348389C20.6092 0.116211 20.0245 0 19.4166 0C18.8088 0 18.224 0.116211 17.6623 0.348389C17.1006 0.580566 16.6046 0.911377 16.1742 1.34082L5.34097 12.1741C5.03819 12.4778 4.86284 12.8425 4.81486 13.2686L4.32854 17.646C4.26806 18.1887 4.41979 18.6638 4.78355 19.071C5.14737 19.4783 5.60233 19.6824 6.1484 19.6831C6.21712 19.6833 6.28567 19.6794 6.35395 19.6714L10.7312 19.1851C11.1568 19.137 11.5225 18.9614 11.8257 18.6589L22.6589 7.82544C23.0883 7.39526 23.4192 6.89917 23.6515 6.3374C23.8838 5.77588 24 5.19092 24 4.58301C24 3.97534 23.8838 3.39038 23.6515 2.82886C23.4192 2.26709 23.0883 1.771 22.6589 1.34082ZM10.5288 17.3621L6.15108 17.8484L6.63783 13.4709L14.8408 5.26807L18.7317 9.15918L10.5288 17.3621ZM21.3621 6.52856L20.0286 7.8623L16.1375 3.97119L17.4711 2.6377C18.0084 2.10059 18.6569 1.83228 19.4164 1.83228C20.176 1.83252 20.8244 2.10107 21.3614 2.63818C21.8985 3.17529 22.1671 3.82373 22.1673 4.58325C22.1674 5.34277 21.899 5.99121 21.3621 6.52856Z" fill="url(#paint0_linear_2455_84)" />
              <path d="M0 4.74072V21.2488C0.000854478 22.0081 0.269709 22.6565 0.80675 23.1934C1.34373 23.7305 1.99191 23.9993 2.75129 24H19.2592C20.0186 23.9993 20.6668 23.7305 21.2037 23.1934C21.7407 22.6565 22.0096 22.0081 22.0105 21.2488V14.5813C22.0105 13.97 21.7047 13.6641 21.0934 13.6641C20.482 13.6641 20.1763 13.97 20.1763 14.5813V21.2488C20.1755 21.8594 19.8698 22.165 19.2592 22.1658H2.75129C2.14071 22.165 1.83499 21.8594 1.8342 21.2488V4.74072C1.83499 4.12988 2.14071 3.82422 2.75129 3.82349H9.41866C10.03 3.82349 10.3357 3.51782 10.3357 2.90649C10.3357 2.29492 10.03 1.98926 9.41866 1.98926H2.75129C1.99191 1.98999 1.34373 2.25903 0.80675 2.7959C0.269709 3.33301 0.000854478 3.9812 0 4.74072Z" fill="url(#paint1_linear_2455_84)" />
              <defs>
                <linearGradient id="paint0_linear_2455_84" x1="0" y1="0" x2="24.8573" y2="0.923472" gradientUnits="userSpaceOnUse">
                  <stop offset="0.0001" stop-color="#F7C100" />
                  <stop offset="1" stop-color="#F69400" />
                </linearGradient>
                <linearGradient id="paint1_linear_2455_84" x1="0" y1="0" x2="24.8573" y2="0.923472" gradientUnits="userSpaceOnUse">
                  <stop offset="0.0001" stop-color="#F7C100" />
                  <stop offset="1" stop-color="#F69400" />
                </linearGradient>
              </defs>
            </svg>
          </span>
          <span @click="removeBlock(block, index)" class="remove-block-btn"><svg width="23" height="24" viewBox="0 0 23 24" fill="none" xmlns="http://www.w3.org/2000/svg">
            <path fill-rule="evenodd" clip-rule="evenodd" d="M0.958355 5.53857C0.31943 5.53857 0 5.23096 0 4.61548C0 4 0.31943 3.69238 0.958355 3.69238H5.96013L6.55533 1.39819C6.67691 0.927654 6.8948 0.575849 7.209 0.342775C7.51789 0.113772 7.91984 -0.00048589 8.41487 2.39165e-06H14.5851C15.5836 -0.00121831 16.2035 0.464846 16.4447 1.39819L17.0399 3.69238H22.0416C22.6806 3.69238 23 4 23 4.61548C23 5.23096 22.6237 5.53857 21.9849 5.53857C21.9849 5.55225 21.9861 5.56592 21.9874 5.57959C21.989 5.59668 21.9906 5.61353 21.9898 5.63086L21.1503 21.8071C21.1189 22.4216 20.872 22.9407 20.4094 23.3647C19.9467 23.7886 19.3962 24.0005 18.7577 24H4.24231C3.60383 24.0005 3.05334 23.7886 2.59073 23.365C2.12817 22.9409 1.88117 22.4221 1.84977 21.8076L0.958355 5.53857ZM14.5851 1.84619H8.41487L7.93573 3.69238H15.0643L14.5851 1.84619ZM20.0759 5.53857H2.92272L3.76362 21.7153C3.77892 22.0076 3.93851 22.1538 4.24231 22.1538H18.7577C19.0618 22.1536 19.2213 22.0073 19.2364 21.7148L20.0759 5.53857ZM8.625 17.5386C8.625 18.1538 8.30557 18.4614 7.66664 18.4614C7.02779 18.4614 6.70836 18.1538 6.70836 17.5386V10.1538C6.70836 9.53833 7.02779 9.23071 7.66664 9.23071C8.30557 9.23071 8.625 9.53833 8.625 10.1538V17.5386ZM12.4584 17.5386C12.4584 18.1538 12.1389 18.4614 11.5 18.4614C10.8611 18.4614 10.5416 18.1538 10.5416 17.5386V10.1538C10.5416 9.53833 10.8611 9.23071 11.5 9.23071C12.1389 9.23071 12.4584 9.53833 12.4584 10.1538V17.5386ZM15.3334 18.4614C15.9722 18.4614 16.2916 18.1538 16.2916 17.5386V10.1538C16.2916 9.53833 15.9722 9.23071 15.3334 9.23071C14.6944 9.23071 14.375 9.53833 14.375 10.1538V17.5386C14.375 18.1538 14.6944 18.4614 15.3334 18.4614Z" fill="url(#paint0_linear_2455_83)" />
            <defs>
              <linearGradient id="paint0_linear_2455_83" x1="0" y1="0" x2="23.8243" y2="0.848214" gradientUnits="userSpaceOnUse">
                <stop offset="0.0001" stop-color="#F7C100" />
                <stop offset="1" stop-color="#F69400" />
              </linearGradient>
            </defs>
          </svg></span>
        </div>
      </div>
    </div>

    <a-modal
        v-model:visible="modalVisible"
        :title="null"
        @ok="handleOk"
        @cancel="handleCancel"
        :destroyOnClose="false"
    >
      <component :is="getTypeFormComponent" v-model="currentBlock"/>
    </a-modal>
  </div>
</template>

<style lang="scss" scoped>
.edit-block-btn, .remove-block-btn {
	cursor: pointer;
	display: flex;
}
.edit-block-btn svg, .remove-block-btn svg {
	width: 17px;
}

.block-wrapper {
  position: relative;
  padding: 10px;
  
}
.block-wrapper:hover {
  border: 1px solid #ccc;
  border-radius: 5px;
}

.block-controls {
  position: absolute;
	display: none;
  top: 10px;
  right: 10px;
	align-items: center;
	height: 17px;
  gap: 10px;
}
.block-wrapper:hover > .block-controls {
	display: flex;
}

.block-wrapper:hover .block-controls svg path {
	fill: #f7c100;
}

.move-block-btn {
  background-color: #1890ff;
  color: white;
  border: none;
  font-size: 11px;
	width: 18px;
	height: 18px;
  padding: 5px 5px;
  border-radius: 3px;
	display: flex;
	align-items: center;
  cursor: pointer;
}

.move-block-btn:hover,
.remove-block-btn:hover {
  opacity: 0.8;
}

.move-block-btn:disabled {
  background-color: #d9d9d9;
  cursor: not-allowed;
}



.catalog_users__wrapper {
  margin-top: 15px;
}
</style>